import { Component, ViewChild, ViewContainerRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { percentReuseEOLerr } from '@piqet-angular/shared';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';
import { ToastrService } from 'ngx-toastr';

const KEY_BACKSPACE = 'Backspace';
const KEY_DELETE = 'Delete';
const KEY_ENTER = 'Enter';
const KEY_TAB = 'Tab';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'numeric-cell',
  templateUrl: './numeric-cell-editor.html',
  styleUrls: ['./numeric-cell-editor.css'],
})
// eslint-disable-next-line @angular-eslint/component-class-suffix
export class NumericCellEditor implements ICellEditorAngularComp {
  params: any;
  public value!: any;
  private cancelBeforeStart = false;
  numberValue: number | undefined;
  isCheckTransportDistance = false;
  isInputFocused: boolean = false;
  activatedRoute: any;
  inlineObj = {};
  width: number = 0;

  constructor(public route: ActivatedRoute, private toasterService: ToastrService) {}

  @ViewChild('input', { read: ViewContainerRef })
  public input!: ViewContainerRef;
  from: any;
  typeColumn: string = '';
  agInit(params: any): void {
    this.params = params;
    this.from = params.from || null;
    this.typeColumn = params.typeColumn || null;
    this.isCheckTransportDistance = this.params.isCheckTransportDistance;
    this.setInitialState(this.params);
    this.activatedRoute = this.route.snapshot.routeConfig?.path;
    // only start edit if key pressed is a number, not a letter
    const allowedStartChars =
      this.from === 'materialIngredient-factorValue' ? '1234567890-' : '1234567890';
    this.cancelBeforeStart = !!(params.charPress && allowedStartChars.indexOf(params.charPress) < 0);
    switch (this.from) {
      case 'materialIngredient-scrapedWeight':
        this.width = 200;
        break;
      case 'materialIngredient-weight':
        this.width = 130;
        break;
      case 'materialIngredient-recycledContent':
        this.width = 210;
        break;
      case 'materialIngredient-amount':
        this.width = 270;
        break;
      case 'custom-filling-amount':
        this.width = 630;
        break;
      case 'conversion-amount':
        this.width = 300;
        break;
      case 'packageEOL':
        this.width = 175;
        break;
      case 'ingredient-weight':
        this.width = 180;
        break;
      case 'orgWeightings':
        this.width = 240;
        break;
      case 'project-pages-inline':
        this.width = 160;
        break;
      case 'packaging-materials':
        this.width = 290;
        break;
      case 'prodLoss':
        this.width = 340;
        break;
      default:
        this.width = 435;
        break;
    }
  }

  setInitialState(params: ICellEditorParams) {
    let startValue;

    if (params.eventKey === KEY_BACKSPACE || params.eventKey === KEY_DELETE) {
      // if backspace or delete pressed, we clear the cell
      startValue = '';
    } else if (params.charPress) {
      // if a letter was pressed, we start with the letter
      startValue = params.charPress;
    } else {
      // otherwise we start with the current value
      startValue = params.value;
    }
    if (this.isCheckTransportDistance) {
      this.value = startValue?.substring(0, startValue.indexOf(' '));
    } else {
      this.value = startValue;
    }
  }

  getValue(): any {
    if (this.params?.from === 'orgWeightings') {
      const numValue = parseFloat(this.value);
      if (isNaN(numValue) || numValue < 0 || numValue > 100) {
        return null;
      }
      return numValue;
    }
    return this.value != '' && this.value != '.' ? this.value : 0;
  }

  isCancelBeforeStart(): boolean {
    return this.cancelBeforeStart;
  }

  // will reject the number if it greater than 1,000,000
  // not very practical, but demonstrates the method.
  isCancelAfterEnd(): any {
    if (this.params.colDef.colId == 'recycledContent') {
      return this.value > 100;
    }
  }

  onKeyDown(event: any) {
    const inputValue = (event.target as HTMLInputElement).value;
    if (event.key === 'Escape') {
      return;
    }

    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
    ];
    if (allowedKeys.includes(event.key)) {
      return true;
    }

    if (
      this.params?.type == 'reuseComponent' &&
      inputValue.length >= 2 &&
      event.key !== 'Backspace' &&
      event.key !== 'Delete'
    ) {
      event.preventDefault();
      return false;
    }

    if (this.params?.from == 'packageEOL' && (event.key === '.' || event.key === ',')) {
      event.preventDefault();
      return false;
    }

    const charCode = event.which ? event.which : event.keyCode;
    if (charCode === 46) {
      if (inputValue.includes('.')) {
        event.preventDefault();
        return false;
      }
      return true;
    }

    if (charCode === 45 && this.from === 'materialIngredient-factorValue') {
      const cursorPos = (event.target as HTMLInputElement).selectionStart ?? 0;
      if (cursorPos !== 0 || inputValue.includes('-')) {
        event.preventDefault();
        return false;
      }
      return true;
    }

    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      event.preventDefault();
      return false;
    }

    if (this.params?.from === 'orgWeightings') {
      const currentValue = inputValue;
      const selectionStart = (event.target as HTMLInputElement).selectionStart || 0;
      const selectionEnd = (event.target as HTMLInputElement).selectionEnd || 0;

      const newValue =
        currentValue.substring(0, selectionStart) +
        event.key +
        currentValue.substring(selectionEnd);
      const numericValue = parseFloat(newValue);

      if (!isNaN(numericValue) && numericValue > 100) {
        event.preventDefault();
        return false;
      }
    }

    const inputElement = event.target as HTMLInputElement;
    const value = inputElement.value + event.key;

    if (this.params.colDef.colId === 'PercentageOfReuse') {
      const numericValue = parseFloat(value);
      if (numericValue > 99.99) {
        event.preventDefault();
      }
    }

    return true;
  }

  onPaste(event: ClipboardEvent): void {
    const pastedText: any = event.clipboardData?.getData('text');

    if (this.params?.from === 'orgWeightings') {
      const numericValue = parseFloat(pastedText);
      if (isNaN(numericValue) || numericValue < 0 || numericValue > 100) {
        this.toasterService.error('Custom weighting must be between 0 and 100');
        event.preventDefault();
        return;
      }
    }

    if (parseInt(pastedText) > 99.99 && this.params.colDef.colId === 'PercentageOfReuse') {
      this.toasterService.error(percentReuseEOLerr);
      event.preventDefault();
      return;
    }

    if (this.params?.from == 'packageEOL' && pastedText.includes('.')) {
      event.preventDefault();
      return;
    }

    if (!this.isValidNumberInput(pastedText)) {
      event.preventDefault();
    }
  }

  onInput(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;

    if (!this.isValidNumberInput(inputValue)) {
      this.numberValue = undefined;
    }
  }

  manageFocus() {
    const ids = [
      'currentDropdown1',
      'inline-ng-select',
      'currentDropdown2',
      'currentDropdown3',
      'ingredientCountryDrp',
      'unitNameDrp',
      'supplierNameDrp',
    ];
    ids.forEach(id => {
      const element = document.getElementById(id);
      if (element) {
        element.classList.remove('focused');
      }
    });
    this.isInputFocused = true;
  }

  onInputFocus() {
    this.isInputFocused = true;
  }

  onInputBlur(eve: any) {
    this.isInputFocused = false;

    if (this.params?.from === 'orgWeightings') {
      const numValue = parseFloat(eve.target.value);
      if (eve.target.value && (isNaN(numValue) || numValue < 0 || numValue > 100)) {
        this.toasterService.error('Custom weighting must be between 0 and 100');
        if (this.params.value) {
          this.value = this.params.value;
          eve.target.value = this.params.value;
        } else {
          this.value = '';
          eve.target.value = '';
        }
        return;
      }
    }

    if (this.activatedRoute == 'end-of-life' && !this.params.data.reuseFillingComponentDetailId) {
      const existingDataString =
        this.params.columnApi.columnModel.columnDefs[18].cellRendererParams.gridType == 'isConsumer'
          ? localStorage.getItem('consumerEOL')
          : localStorage.getItem('retailEOL');
      const existingData = existingDataString ? JSON.parse(existingDataString) : {};
      existingData[this.params.colDef.field] = Number(eve.target.value);
      existingData['materialId'] = Number(this.params.node?.data?.materialId);
      existingData['materialName'] = this.params.node?.data?.materialName;
      Object.assign(this.inlineObj, existingData);
      this.updateLocalStorage(
        this.params.columnApi?.columnModel?.columnDefs[18]?.cellRendererParams?.gridType
      );
    } else if (this.activatedRoute == 'product') {
      this.params.editData(Number(eve.target.value));
    }
  }

  updateLocalStorage(gridType: any) {
    gridType == 'isConsumer'
      ? localStorage.setItem('consumerEOL', JSON.stringify(this.inlineObj))
      : localStorage.setItem('retailEOL', JSON.stringify(this.inlineObj));
  }

  isValidNumberInput(value: string | undefined): boolean {
    if (value === undefined) {
      return false;
    }

    const numericValue = Number(value);

    if (this.from === 'materialIngredient-factorValue') {
      return !isNaN(numericValue);
    }

    // Check if the numeric value is a valid number and greater than or equal to 0
    return !isNaN(numericValue) && numericValue >= 0;
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.from == 'project-pages-inline') this.input.element.nativeElement.focus();
    }, 0);
  }
}
